// src/context/DashboardDefaultsContext.tsx
/**
 * Holds the platform-wide dashboard default range + wide-prefetch
 * constants fetched from `GET /api/config/dashboard-defaults`. Fetched
 * once after the user is authenticated; consumers read via
 * `useDashboardDefaults()`.
 *
 * If the fetch fails or hasn't resolved yet, the context returns a
 * hardcoded fallback matching the v1 backend default (`last_month`).
 * That keeps the dashboard usable on degraded backends without
 * skeleton-blocking the whole app.
 *
 * Single-source-of-truth contract: changing the default range becomes a
 * one-line edit in `apps/backend/services/dashboard_cache_config.py` +
 * `make generate-types`. The frontend picks up the new value on the next
 * Insights page mount.
 *
 * Plan: docs/plans-pending/2026-05-19-server-side-dashboard-cache-refill.md
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { useAuth } from "@/context/AuthContext";
import { apiData } from "@/api/client";

type TimeRangePreset =
  | "today"
  | "yesterday"
  | "last_7_days"
  | "last_14_days"
  | "last_30_days"
  | "last_90_days"
  | "last_365_days"
  | "this_week"
  | "last_week"
  | "this_month"
  | "last_month"
  | "this_quarter"
  | "last_quarter"
  | "this_year"
  | "last_year";

type TimeGrain = "daily" | "weekly" | "monthly" | "quarterly" | "yearly";

export type DashboardDefaults = {
  defaultRangeName: TimeRangePreset;
  widePrefetchDays: number;
  widePrefetchGrain: TimeGrain;
  isReady: boolean;
};

// Hardcoded fallback — MUST match the backend's DEFAULT_RANGE_NAME +
// WIDE_PREFETCH_DAYS/GRAIN constants. Cross-language fixture
// `apps/backend/tests/fixtures/dashboard-default-range.json` locks the
// `last_month` contract.
const FALLBACK: DashboardDefaults = {
  defaultRangeName: "last_month",
  widePrefetchDays: 730,
  widePrefetchGrain: "daily",
  isReady: false,
};

const DashboardDefaultsContext = createContext<DashboardDefaults>(FALLBACK);

export function DashboardDefaultsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [defaults, setDefaults] = useState<DashboardDefaults>(FALLBACK);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    (async () => {
      try {
        // Route through the shared API client so this hits the same
        // VITE_API_URL base (with the auth cookie) as every other call and
        // participates in refresh-on-401. A raw fetch against a different
        // base silently 401'd forever and kept the fallback (see the
        // dashboard-defaults 401 storm). On any failure we keep the
        // hardcoded FALLBACK already in state.
        const data = await apiData<{
          default_range_name: TimeRangePreset;
          wide_prefetch_days: number;
          wide_prefetch_grain: TimeGrain;
        }>("/config/dashboard-defaults");
        if (cancelled) return;
        setDefaults({
          defaultRangeName: data.default_range_name,
          widePrefetchDays: data.wide_prefetch_days,
          widePrefetchGrain: data.wide_prefetch_grain,
          isReady: true,
        });
      } catch {
        // Auth/network failure — fallback already in state
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user]);

  return (
    <DashboardDefaultsContext.Provider value={defaults}>
      {children}
    </DashboardDefaultsContext.Provider>
  );
}

export function useDashboardDefaults(): DashboardDefaults {
  return useContext(DashboardDefaultsContext);
}
